import { type } from 'arktype';
import type { TranslatableText } from '@zag/core/common';
import { translatableTextSchema } from '@zag/core/models';
import { shiftRulesSchema, shiftSkillEntrySchema, type ShiftSkillEntry } from './shift-rules.model';

export const SHIFT_TRANSLATABLE_FIELDS = ['name'] as const;
export type ShiftTranslatableField = (typeof SHIFT_TRANSLATABLE_FIELDS)[number];
export type ShiftTranslations = TranslatableText<ShiftTranslatableField>;

export const shiftQuerySchema = type({
  'strategyId?': 'string',
  'planId?': 'string',
  'name?': 'string',
  'type?': 'string',
  'status?': 'string',
});

export type ShiftQuery = typeof shiftQuerySchema.t;

/**
 * Schema for creating a shift.
 *
 * `rules` is optional at the schema level because it can be inherited from
 * a definition when `definitionId` is provided. The service enforces that
 * rules must be present (either from request or definition) before creating.
 */
export const createShiftSchema = type({
  'strategyId?': type('string').configure({
    description: 'ID of the planning strategy this shift belongs to.',
    examples: ['strategy_abc123'],
  }),
  'planId?': type('string').configure({
    description: 'Optional plan ID when the shift is scoped to a specific plan.',
    examples: ['plan_abc123'],
  }),
  'name?': type('string').configure({
    description: 'Display name of the shift.',
    examples: ['Early', 'Late'],
  }),
  'startTime?': type('string').configure({
    description: 'Start time of the shift as an `HH:mm` 24-hour clock string.',
    examples: ['06:00', '08:00'],
  }),
  'endTime?': type('string').configure({
    description: 'End time of the shift as an `HH:mm` 24-hour clock string.',
    examples: ['14:00', '16:30'],
  }),
  'breakMinutes?': type('number>=0 | null').configure({
    description: 'Unpaid break during the shift, in minutes. `null` clears the value.',
    examples: [30],
  }),
  'defaultRoleId?': type('string | null').configure({
    description: 'Optional job-role ID applied to assignments made against this shift by default.',
    examples: ['jobrole_abc123'],
  }),
  'status?': type('string').configure({
    description: "Lifecycle status of the shift (e.g. 'active', 'inactive').",
    examples: ['active'],
  }),
  'definitionId?': type('string').configure({
    description: 'Optional shift-definition ID. When provided, rules are inherited from the definition.',
    examples: ['shiftdef_abc123'],
  }),
  'rules?': shiftRulesSchema.configure({
    description: 'Shift rules (weekdays, capacity, required skills). Required unless inherited from a definition.',
  }),
  'capacity?': type('number>=0').configure({
    description: 'Convenience top-level capacity. Merged into `rules.capacity` when provided.',
    examples: [3],
  }),
  'requiredSkills?': shiftSkillEntrySchema.array().configure({
    description:
      'Convenience top-level required skills (plain IDs or structured entries with a minimum rating). Merged into `rules.requiredSkills` when provided.',
    examples: [['skill_abc123', { skillId: 'skill_def456', minRating: 0.6 }]],
  }),
  'allowOverlap?': type('boolean').configure({
    description: 'Convenience top-level overlap flag. Merged into `rules.allowOverlap` when provided.',
    examples: [false],
  }),
  'icon?': type('string | null').configure({
    description: 'Material Icons identifier for shift chips. `null` falls back to a default icon.',
    examples: ['wb_sunny'],
  }),
  'color?': type('string | null').configure({
    description: 'CSS accent colour, usually a six-digit hex. `null` falls back to the default palette.',
    examples: ['#0055aa'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'Illustrative image URL.',
  }),
  'data?': type('object | null').configure({
    description:
      'Free-form JSON payload attached to the shift. Reserved keys: `customProperties` (record of values keyed by ' +
      'custom-property-definition keys for `entityType=shift`). Values are validated against the registered definitions.',
    examples: [{ customProperties: { bookable: true, defaultLocationId: 'loc_abc123' } }],
  }),
  'translations?': translatableTextSchema(SHIFT_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations for `name`. When provided, ' +
      '`translations.en.name` is mirrored into the legacy column for back-compat.',
  }),
  'balanceType?': type('string | null').configure({
    description: 'Balance type key this shift contributes to. `null` means no balance effect. Unit-level override takes precedence.',
    examples: ['flexitime', null],
  }),
}).configure({ description: 'Payload for creating a shift. Either `rules` or `definitionId` must resolve to a concrete rules set.' });

export type CreateShift = typeof createShiftSchema.t;

export const updateShiftSchema = type({
  'strategyId?': type('string').configure({
    description: 'Updated planning strategy ID.',
    examples: ['strategy_abc123'],
  }),
  'name?': type('string').configure({
    description: 'Updated display name of the shift.',
    examples: ['Early'],
  }),
  'startTime?': type('string').configure({
    description: 'Updated start time as `HH:mm`.',
    examples: ['06:00'],
  }),
  'endTime?': type('string').configure({
    description: 'Updated end time as `HH:mm`.',
    examples: ['14:00'],
  }),
  'breakMinutes?': type('number>=0 | null').configure({
    description: 'Updated unpaid break in minutes.',
    examples: [30],
  }),
  'defaultRoleId?': type('string | null').configure({
    description: 'Updated default job-role ID.',
    examples: ['jobrole_abc123'],
  }),
  'status?': type('string').configure({
    description: "Updated lifecycle status (e.g. 'active', 'inactive').",
    examples: ['active'],
  }),
  'icon?': type('string | null').configure({
    description: 'Material icon name for shift display.',
    examples: ['schedule'],
  }),
  'color?': type('string | null').configure({
    description: 'Hex colour for shift display.',
    examples: ['#3b82f6'],
  }),
  'imageUrl?': type('string | null').configure({
    description: 'Optional image URL for shift display.',
    examples: ['https://example.com/shift.png'],
  }),
  'capacity?': type('number>=0').configure({
    description: 'Updated capacity (merged into rules.capacity).',
    examples: [3],
  }),
  'requiredSkills?': shiftSkillEntrySchema.array().configure({
    description: 'Updated required skills (plain IDs or structured entries with a minimum rating, merged into rules.requiredSkills).',
    examples: [['skill_abc123', { skillId: 'skill_def456', minRating: 0.6 }]],
  }),
  'allowOverlap?': type('boolean').configure({
    description: 'When true, assignments for this shift may overlap in time with other shifts (merged into rules.allowOverlap).',
    examples: [false],
  }),
  'rules?': shiftRulesSchema.configure({
    description:
      'Replacement shift rules (weekdays, capacity, required skills). Use top-level `capacity`/`requiredSkills`/`allowOverlap` for partial updates.',
  }),
  'data?': type('object | null').configure({
    description:
      'Free-form JSON payload to merge into the shift. The `customProperties` sub-object is deep-merged with the ' +
      'existing values (top-level data fields are replaced).',
    examples: [{ customProperties: { bookable: true } }],
  }),
  'translations?': translatableTextSchema(SHIFT_TRANSLATABLE_FIELDS).configure({
    description:
      'Per-language translations to merge into the existing translations map. ' +
      'Languages and fields omitted are preserved. Setting a field to `null` clears it.',
  }),
  'balanceType?': type('string | null').configure({
    description: 'Balance type key this shift contributes to. `null` clears the balance effect.',
    examples: ['flexitime', null],
  }),
}).configure({ description: 'Partial update of a shift — fields omitted from the body remain unchanged.' });

export type UpdateShift = typeof updateShiftSchema.t;

/**
 * Shift response type returned by the API.
 */
export interface ShiftResponse {
  id: string;
  strategyId: string;
  name: string | null;
  startTime: string;
  endTime: string;
  status: string;
  breakMinutes: number | null;
  defaultRoleId: string | null;
  requiredSkills: ShiftSkillEntry[] | null;
  capacity: number | null;
  data: Record<string, unknown> | null;
  icon?: string | null;
  color?: string | null;
  imageUrl?: string | null;
  /** Number of active (non-deleted) shift units attached to this shift definition. */
  shiftUnitsCount: number;
  /** Active shift units. Populated by the list endpoint. */
  shiftUnits?: Array<{ id: string; shiftId: string; startTime: string; endTime: string; createdAt: string; updatedAt: string }>;
  /** Full per-language translations map. Only populated when the caller asked for it via `?include=translations`. */
  translations?: TranslatableText<string>;
  createdAt: string;
  updatedAt: string;
}
